import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Icon, Spinner, NonIdealState } from "@blueprintjs/core";
import { api, type DashboardResponse } from "../lib/api";

const ACTION_LABELS: Record<string, { text: string; dot: string }> = {
  created: { text: "created", dot: "blue" },
  updated: { text: "updated", dot: "blue" },
  archived: { text: "archived", dot: "orange" },
  unarchived: { text: "restored", dot: "green" },
  member_added: { text: "added a member to", dot: "purple" },
  member_removed: { text: "removed a member from", dot: "orange" },
  pipeline_completed: { text: "completed pipeline for", dot: "green" },
  pipeline_started: { text: "started pipeline for", dot: "blue" },
  pipeline_failed: { text: "pipeline failed for", dot: "red" },
  lead_claimed: { text: "claimed a lead in", dot: "orange" },
  lead_updated: { text: "updated a lead in", dot: "blue" },
  status_changed: { text: "changed status of", dot: "blue" },
  note_added: { text: "added a note on", dot: "purple" },
  follow_up_created: { text: "scheduled follow-up for", dot: "blue" },
  follow_up_completed: { text: "completed follow-up for", dot: "green" },
};

function formatAction(action: string): { text: string; dot: string } {
  return ACTION_LABELS[action] || { text: action.replace(/_/g, " "), dot: "blue" };
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get<DashboardResponse>("/dashboard/")
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <Spinner size={32} />
      </div>
    );
  }

  if (error) {
    return <NonIdealState icon="error" title="Failed to load dashboard" description={error} />;
  }

  const m = data?.metrics;

  return (
    <div>
      <div className="dash-header">
        <div>
          <h2>Dashboard</h2>
          <p>Lead generation overview and pipeline analytics</p>
        </div>
        <div className="dash-header-actions">
          <Button icon="add" intent="primary" onClick={() => navigate("/blueprints")}>
            New Blueprint
          </Button>
        </div>
      </div>

      <div className="metrics-grid">
        <MetricCard label="Total Leads" value={m?.total_leads ?? 0} icon="people" />
        <MetricCard label="Qualified Leads" value={m?.qualified_leads ?? 0} icon="tick-circle" />
        <MetricCard label="Active Blueprints" value={m?.active_blueprints ?? 0} icon="map-create" />
        <MetricCard label="Conversion Rate" value={`${m?.conversion_rate ?? 0}%`} icon="percentage" />
      </div>

      <div className="dash-panels dash-panels--full">
        <div className="dash-panel">
          <div className="dash-panel-header">
            <h4>Recent Activity</h4>
          </div>
          <div className="dash-panel-body">
            {data?.recent_activities && data.recent_activities.length > 0 ? (
              <ul className="activity-list">
                {data.recent_activities.map((a) => {
                  const { text, dot } = formatAction(a.action);
                  return (
                    <li key={a.id} className="activity-item">
                      <div className={`activity-dot ${dot}`} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="activity-text">
                          <strong>{a.user_name}</strong> {text}{" "}
                          {a.entity_name && a.entity_id ? (
                            <span
                              className="activity-entity-link"
                              onClick={() => navigate(`/blueprints/${a.entity_id}`)}
                            >{a.entity_name}</span>
                          ) : a.entity_type}
                        </div>
                        <div className="activity-time">
                          {relativeTime(a.created_at)}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <NonIdealState
                icon="timeline-events"
                title="No activity yet"
                description="Activity will appear here as you create blueprints and work with leads."
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, icon }: { label: string; value: number | string; icon: string }) {
  return (
    <div className="metric-card">
      <div className="metric-card-header">
        <span className="metric-card-label">{label}</span>
        <Icon icon={icon as any} size={16} color="#5c7080" />
      </div>
      <div className="metric-card-value">{typeof value === "number" ? value.toLocaleString() : value}</div>
    </div>
  );
}

